import { endSessionAndRedirectToLogin, clearLocalAuthState } from './utils/authLogout';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const AUTH_REQUIRED_ERROR = 'auth_required';
const SESSION_REVOKED_ERROR = 'Session revoked or expired';

const clearAuthStorage = () => {
  clearLocalAuthState();
};

const isAuthRequiredError = (error) => (error?.message || '') === AUTH_REQUIRED_ERROR;

async function getResponseError(response) {
  try {
    const payload = await response.clone().json();
    return payload?.error || '';
  } catch (_err) {
    return '';
  }
}

export async function secureFetch(path, options = {}) {
  let token = localStorage.getItem('developmentexpress_token');
  if (!token) {
    clearAuthStorage();
    throw new Error(AUTH_REQUIRED_ERROR);
  }
  const execute = async (activeToken) => {
    const headers = {
      Authorization: `Bearer ${activeToken || ''}`,
      ...(options.headers || {}),
    };
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });
  };

  let response = await execute(token);
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('developmentexpress_refresh_token');
    if (refreshToken) {
      const refreshResponse = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (refreshResponse.ok) {
        const refreshPayload = await refreshResponse.json().catch(() => ({}));
        if (refreshPayload?.token) {
          localStorage.setItem('developmentexpress_token', refreshPayload.token);
          if (refreshPayload.refreshToken) {
            localStorage.setItem('developmentexpress_refresh_token', refreshPayload.refreshToken);
          }
          token = refreshPayload.token;
          response = await execute(token);
        }
      }
    }
    if (response.status === 401) {
      const responseError = await getResponseError(response);
      endSessionAndRedirectToLogin({
        sessionRevoked: responseError === SESSION_REVOKED_ERROR,
      });
      throw new Error(AUTH_REQUIRED_ERROR);
    }
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (payload && typeof payload.success === 'boolean') {
      return payload;
    }
    throw new Error(payload.error || payload.message || `Request failed: ${response.status}`);
  }
  return payload;
}

async function fetchAdminPage(endpoint, { limit = 250, offset = 0 } = {}) {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const result = await secureFetch(`${endpoint}?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      error: '',
    };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || `Failed to fetch ${endpoint}` };
  }
}

// ─── MACHINES ───
export const getMachines = async (params = {}) => {
  try {
    const token = localStorage.getItem('developmentexpress_token');
    const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
    if (token && user?.role === 'admin') {
      const result = await secureFetch('/api/admin/machines');
      return result.items || [];
    }
    if (token && user?.role === 'client') {
      const qs = new URLSearchParams();
      const { type, hours, date, lat, lng, radius, limit, ranking } = params || {};
      if (type) qs.set('type', String(type));
      if (hours != null && String(hours).trim() !== '') qs.set('hours', String(hours));
      if (date) qs.set('date', String(date));
      if (lat != null && lat !== '' && Number.isFinite(Number(lat))) qs.set('lat', String(lat));
      if (lng != null && lng !== '' && Number.isFinite(Number(lng))) qs.set('lng', String(lng));
      if (radius != null && String(radius).trim() !== '') qs.set('radius', String(radius));
      if (limit != null && String(limit).trim() !== '') qs.set('limit', String(limit));
      if (ranking === 'job_request') qs.set('ranking', 'job_request');
      const query = qs.toString();
      const result = await secureFetch(`/api/client/machines${query ? `?${query}` : ''}`);
      return (result.machines || []).map((m) => ({
        ...m,
        operator: m.operator
          ? {
              name: m.operator.name || null,
              experience_years: m.operator.experience_years ?? 0,
              experience_machine_types: m.operator.experience_machine_types || [],
              // API returns signed photo_url; ClientDashboard still reads profile_photo_path
              profile_photo_path: m.operator.photo_url || m.operator.profile_photo_path || null,
            }
          : null,
      }));
    }
    if (token && user?.role === 'owner') {
      const result = await secureFetch('/api/owner/machines?limit=500');
      return result.items || [];
    }
  } catch (error) {
    console.error(error);
    return [];
  }
  console.error('getMachines: authentication required or unsupported role');
  return [];
};

export const getMachinesPage = async (options = {}) => fetchAdminPage('/api/admin/machines', options);

// ─── BOOKINGS ───
export const getContractBookingQuote = async ({
  machineId,
  startDate,
  endDate,
  billingCycle,
  fixedRatePerCycle,
  guaranteedHoursPerCycle,
  attachmentRatePerCycle,
  mobilizationTransportBy,
  mobilizationTransportAmount,
  demobilizationTransportBy,
  demobilizationTransportAmount,
}) => secureFetch('/api/bookings/quote', {
  method: 'POST',
  body: JSON.stringify({
    machineId,
    startDate,
    endDate,
    billingCycle,
    fixedRatePerCycle,
    guaranteedHoursPerCycle,
    attachmentRatePerCycle: attachmentRatePerCycle === '' || attachmentRatePerCycle == null
      ? null
      : Number(attachmentRatePerCycle),
    mobilizationTransportBy,
    mobilizationTransportAmount,
    demobilizationTransportBy,
    demobilizationTransportAmount,
  }),
});

export const createContractQuotation = async (payload) => secureFetch('/api/quotations', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const acceptContractQuotationTerms = async (quotationId, {
  acceptTerms = true,
  clientPaymentDueDay,
  acceptPaymentDueDate,
} = {}) => (
  secureFetch(`/api/quotations/${encodeURIComponent(quotationId)}/client-accept`, {
    method: 'POST',
    body: JSON.stringify({
      acceptTerms,
      clientPaymentDueDay,
      acceptPaymentDueDate,
    }),
  })
);

export const cancelContractQuotation = async (quotationId) => (
  secureFetch(`/api/quotations/${encodeURIComponent(quotationId)}/cancel`, {
    method: 'POST',
  })
);

export const clientEsignAndPayQuotation = async (
  quotationId,
  { acceptEsign = true, idempotencyKey } = {},
) => (
  secureFetch(`/api/quotations/${encodeURIComponent(quotationId)}/client-esign`, {
    method: 'POST',
    body: JSON.stringify({ acceptEsign, idempotencyKey }),
  })
);

export const getContractQuotation = async (quotationId) => (
  secureFetch(`/api/quotations/${encodeURIComponent(quotationId)}`)
);

export const getTermsStatus = async () => (
  secureFetch('/api/auth/terms/status')
);

export const acknowledgeTerms = async () => (
  secureFetch('/api/auth/terms/acknowledge', {
    method: 'POST',
    body: JSON.stringify({}),
  })
);

export const listMyContractQuotations = async ({ status, limit } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit != null) params.set('limit', String(limit));
  const qs = params.toString();
  return secureFetch(`/api/quotations${qs ? `?${qs}` : ''}`);
};

export const listOwnerPendingQuotations = async ({ status, limit } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit != null) params.set('limit', String(limit));
  const qs = params.toString();
  return secureFetch(`/api/owner/quotations${qs ? `?${qs}` : ''}`);
};

export const listOwnerAwaitingMobilizationQuotations = async () => (
  listOwnerPendingQuotations({ status: 'awaiting_mobilization' })
);

export const listOwnerMobilizationConfirmedQuotations = async () => (
  listOwnerPendingQuotations({ status: 'mobilization_confirmed' })
);

export const acceptOwnerQuotation = async (quotationId) => (
  secureFetch(`/api/owner/quotations/${encodeURIComponent(quotationId)}/accept`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
);

export const rejectOwnerQuotation = async (quotationId, { rejectReason } = {}) => (
  secureFetch(`/api/owner/quotations/${encodeURIComponent(quotationId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejectReason: rejectReason || null }),
  })
);

export const confirmOwnerMobilization = async (quotationId) => (
  secureFetch(
    `/api/owner/quotations/${encodeURIComponent(quotationId)}/confirm-mobilization`,
    { method: 'POST', body: JSON.stringify({}) },
  )
);

export const confirmClientArrival = async (quotationId, { photoUrl } = {}) => (
  secureFetch(
    `/api/client/quotations/${encodeURIComponent(quotationId)}/confirm-arrival`,
    {
      method: 'POST',
      body: JSON.stringify({ photoUrl: photoUrl || null }),
    },
  )
);

export const convertOwnerQuotation = async (quotationId) => (
  secureFetch(`/api/owner/quotations/${encodeURIComponent(quotationId)}/convert`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
);

export const listAdminQuotationExceptions = async ({ limit } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  const qs = params.toString();
  return secureFetch(`/api/admin/contract-quotations/exceptions${qs ? `?${qs}` : ''}`);
};

export const approveAdminQuotationOverride = async (quotationId, { adminNote } = {}) => (
  secureFetch(`/api/admin/contract-quotations/${encodeURIComponent(quotationId)}/approve-override`, {
    method: 'POST',
    body: JSON.stringify({ adminNote: adminNote || '' }),
  })
);

export const rejectAdminQuotation = async (quotationId, { adminNote } = {}) => (
  secureFetch(`/api/admin/contract-quotations/${encodeURIComponent(quotationId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ adminNote: adminNote || '' }),
  })
);

export const createBooking = async (booking) => {
  try {
    const body = {
      machineId: booking.machine_id,
      bookingType: booking.booking_type,
      bookingUnit: booking.booking_unit,
      quantity: booking.quantity,
      baseAmount: booking.base_amount,
      gstAmount: booking.gst_amount,
      totalAmount: booking.total_amount,
      advancePaid: booking.advance_paid,
      location: booking.location,
      startDate: booking.start_date,
      endDate: booking.end_date || null,
      billingCycle: booking.billing_cycle || null,
      fixedRatePerCycle: booking.fixed_rate_per_cycle ?? null,
      guaranteedHoursPerCycle: booking.guaranteed_hours_per_cycle ?? null,
      attachmentRatePerCycle: booking.attachment_rate_per_cycle ?? null,
      mobilizationTransportBy: booking.mobilization_transport_by || null,
      mobilizationTransportAmount: booking.mobilization_transport_amount ?? null,
      demobilizationTransportBy: booking.demobilization_transport_by || null,
      demobilizationTransportAmount: booking.demobilization_transport_amount ?? null,
      idempotency_key: booking.idempotency_key,
    };
    const siteAddress = booking.site_address != null ? String(booking.site_address).trim() : '';
    if (siteAddress) {
      body.site_address = siteAddress;
      const sitePincode = booking.site_pincode != null ? String(booking.site_pincode).trim() : '';
      if (sitePincode) body.site_pincode = sitePincode;
      if (Number.isFinite(Number(booking.site_lat))) body.site_lat = Number(booking.site_lat);
      if (Number.isFinite(Number(booking.site_lng))) body.site_lng = Number(booking.site_lng);
      if (booking.location_source) body.location_source = booking.location_source;
    }
    if (booking.booked_hours != null) body.bookedHours = Number(booking.booked_hours);
    if (booking.rate_per_hour != null) body.ratePerHour = Number(booking.rate_per_hour);
    if (booking.booking_unit) body.bookingUnit = booking.booking_unit;
    if (booking.minimum_hours != null) body.minimumHours = Number(booking.minimum_hours);
    const selectedAttachment = booking.selected_attachment != null
      ? String(booking.selected_attachment).trim()
      : '';
    body.selected_attachment = selectedAttachment || null;
    if (booking.attachment_usage_hours != null && booking.attachment_usage_hours !== '') {
      const usage = Number(booking.attachment_usage_hours);
      if (Number.isFinite(usage) && usage > 0) body.attachment_usage_hours = usage;
    }
    const siteLocation = booking.site_location != null
      ? String(booking.site_location).trim()
      : '';
    if (siteLocation) body.site_location = siteLocation.slice(0, 500);
    const workDescription = booking.work_description != null
      ? String(booking.work_description).trim()
      : '';
    if (workDescription) body.work_description = workDescription.slice(0, 1000);
    const payload = await secureFetch('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (payload && typeof payload.success === 'boolean') {
      return payload;
    }
    if (payload?.booking) {
      return {
        success: true,
        code: payload.status === 'already_processed' ? 'DUPLICATE' : 'OK',
        message: 'Booking created',
        data: {
          booking: payload.booking,
          new_balance: payload.newBalance,
          booking_ref: payload.booking?.booking_ref,
        },
      };
    }
    return { success: false, code: 'INTERNAL_ERROR', message: 'Unexpected booking response' };
  } catch (error) {
    console.error(error);
    if (isAuthRequiredError(error)) {
      return { success: false, code: 'AUTH_REQUIRED', message: error.message };
    }
    return {
      success: false,
      code: 'NETWORK_ERROR',
      message: error?.message || 'Booking request failed',
    };
  }
};

export const getSavedAddresses = async () => {
  try {
    const result = await secureFetch('/api/client/saved-addresses');
    return { addresses: Array.isArray(result.addresses) ? result.addresses.slice(0, 5) : [], error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { addresses: [], error: error?.message || 'Failed to load saved addresses' };
  }
};

export const saveAddress = async ({ address, pincode, lat, lng, label } = {}) => {
  try {
    const result = await secureFetch('/api/client/saved-addresses', {
      method: 'POST',
      body: JSON.stringify({
        address,
        pincode: pincode || null,
        lat: Number.isFinite(Number(lat)) && lat != null && lat !== '' ? Number(lat) : null,
        lng: Number.isFinite(Number(lng)) && lng != null && lng !== '' ? Number(lng) : null,
        label: label || null,
      }),
    });
    return { ok: true, address: result.address || null, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { ok: false, address: null, error: error?.message || 'Failed to save address' };
  }
};

export const getBookingsByClient = async (clientId) => {
  try {
    const result = await secureFetch('/api/bookings/me');
    return (result.items || []).filter((item) => item.client_id === clientId);
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const cancelClientBooking = async (bookingId) => {
  try {
    const payload = await secureFetch(`/api/bookings/${encodeURIComponent(bookingId)}/cancel`, {
      method: 'POST',
    });
    return payload;
  } catch (err) {
    return { success: false, error: err.message || 'Cancellation failed' };
  }
};

export const getMyBookingsPage = async ({ limit = 100, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const result = await secureFetch(`/api/bookings/me?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || 'Failed to fetch bookings' };
  }
};

export const submitBookingFeedback = async (bookingId, { operatorRating, satisfied, suggestion }) => {
  try {
    const payload = await secureFetch(`/api/bookings/${encodeURIComponent(bookingId)}/feedback`, {
      method: 'PATCH',
      body: JSON.stringify({
        operatorRating,
        satisfied,
        suggestion: suggestion || '',
      }),
    });
    return payload?.success ? payload.feedback : null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getAllBookings = async () => {
  try {
    const result = await secureFetch('/api/admin/bookings');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getAllBookingsPage = async (options = {}) => fetchAdminPage('/api/admin/bookings', options);

// ─── WALLET ───
export const getWalletBalance = async (userId) => {
  try {
    const result = await secureFetch(`/api/wallet/${userId}/balance`);
    return result?.balance || 0;
  } catch (error) {
    console.error(error);
    return 0;
  }
};

export const updateWalletBalance = async (userId, amount) => {
  console.warn('updateWalletBalance is deprecated on client. Use backend wallet APIs.');
  return false;
};

// ─── TRANSACTIONS ───
export const addTransaction = async (transaction) => {
  console.warn('addTransaction is deprecated on client. Use backend transactional APIs.');
  return false;
};

export const getTransactionsByUser = async (userId) => {
  try {
    const result = await secureFetch('/api/bookings/me/transactions');
    return (result.items || []).filter((item) => item.user_id === userId);
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getMyTransactionsPage = async ({
  limit = 50,
  offset = 0,
  year,
  month,
  includeOpening,
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (year != null && year !== '') params.set('year', String(year));
    if (month != null && month !== '') params.set('month', String(month));
    if (includeOpening) params.set('include_opening', '1');
    const result = await secureFetch(`/api/bookings/me/transactions?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      year: result.year,
      month: result.month,
      periodStart: result.periodStart || null,
      periodEnd: result.periodEnd || null,
      openingBalance: result.openingBalance,
      closingBalance: result.closingBalance,
      creditsTotal: result.creditsTotal,
      debitsTotal: result.debitsTotal,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || 'Failed to fetch transactions' };
  }
};

export const getAllTransactions = async () => {
  try {
    const result = await secureFetch('/api/admin/transactions');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getAllTransactionsPage = async (options = {}) => fetchAdminPage('/api/admin/transactions', options);

// ─── USERS ───
export const getAllUsersPage = async (options = {}) => fetchAdminPage('/api/admin/users', options);

// ─── FUEL LOGS ───
export const addFuelLog = async (log) => {
  try {
    await secureFetch('/api/operator/fuel-log', {
      method: 'POST',
      body: JSON.stringify({
        fuelLevel: Number(log.fuel_level),
        note: log.note,
      }),
    });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const getFuelLogs = async (machineId) => {
  try {
    const result = await secureFetch('/api/operator/fuel-logs');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getFuelLogsPage = async ({ limit = 50, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const result = await secureFetch(`/api/operator/fuel-logs?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || 'Failed to fetch fuel logs' };
  }
};

// ─── ATTENDANCE ───
export const getTodayAttendance = async () => {
  try {
    const result = await secureFetch('/api/operator/attendance/today');
    return {
      record: result.record || null,
      date: result.date || null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { record: null, date: null, error: error?.message || 'Failed to fetch today attendance' };
  }
};

export const clockInAttendance = async () => {
  try {
    const result = await secureFetch('/api/operator/attendance/clock-in', { method: 'POST' });
    return { success: true, ...result, error: '' };
  } catch (error) {
    console.error(error);
    return { success: false, error: error?.message || 'Failed to clock in' };
  }
};

export const clockOutAttendance = async () => {
  try {
    const result = await secureFetch('/api/operator/attendance/clock-out', { method: 'POST' });
    return { success: true, ...result, error: '' };
  } catch (error) {
    console.error(error);
    return { success: false, error: error?.message || 'Failed to clock out' };
  }
};

export const markAttendance = async (attendance) => {
  try {
    const result = await secureFetch('/api/operator/attendance', {
      method: 'POST',
      body: JSON.stringify({
        status: attendance.status,
        date: attendance.date,
      }),
    });
    return {
      success: true,
      status: result.status,
      auto_converted: Boolean(result.auto_converted),
      reason: result.reason || null,
    };
  } catch (error) {
    console.error(error);
    return {
      success: false,
      status: null,
      auto_converted: false,
      reason: null,
      error: error?.message || 'Failed to mark attendance',
    };
  }
};

export const getOperatorPlQuota = async ({ year } = {}) => {
  try {
    const params = new URLSearchParams();
    if (year != null) params.set('year', String(year));
    const query = params.toString();
    const result = await secureFetch(`/api/operator/pl-quota${query ? `?${query}` : ''}`);
    return {
      success: true,
      quota: result.quota || null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return {
      success: false,
      quota: null,
      error: error?.message || 'Failed to fetch paid leave quota',
    };
  }
};

export const getAttendanceByOperator = async (operatorId) => {
  try {
    const result = await secureFetch('/api/operator/attendance');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getAttendancePage = async ({ limit = 30, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const result = await secureFetch(`/api/operator/attendance?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || 'Failed to fetch attendance' };
  }
};

export const getOperatorBookingsPage = async ({ limit = 50, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const result = await secureFetch(`/api/operator/bookings?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || 'Failed to fetch operator bookings' };
  }
};

export const getOperatorAssignments = async () => {
  try {
    const result = await secureFetch('/api/operator/assignments');
    return { items: result.items || [], error: '' };
  } catch (error) {
    console.error(error);
    return { items: [], error: error?.message || 'Failed to fetch operator assignments' };
  }
};

export const updateOperatorLocation = async (bookingId, status, lat, lng) => {
  try {
    const body = { status };
    if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
      body.lat = Number(lat);
      body.lng = Number(lng);
    }
    const result = await secureFetch(`/api/operator/booking/${bookingId}/location`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { success: true, operator_status: result.operator_status };
  } catch (error) {
    console.error(error);
    return { success: false, error: error?.message || 'Failed to update location' };
  }
};

// ─── ISSUES ───
export const reportIssue = async (issue) => {
  try {
    await secureFetch('/api/operator/issues', {
      method: 'POST',
      body: JSON.stringify({
        issueType: issue.issue_type,
        description: issue.description,
      }),
    });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const getAllIssues = async () => {
  try {
    const result = await secureFetch('/api/admin/issues');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getAllIssuesPage = async (options = {}) => fetchAdminPage('/api/admin/issues', options);

export const getPendingUsers = async () => {
  try {
    const result = await secureFetch('/api/admin/users/pending');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const approveUser = async (userId) => {
  try {
    await secureFetch(`/api/admin/users/${userId}/approve`, { method: 'PATCH' });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const rejectUser = async (userId, reason) => {
  try {
    await secureFetch(`/api/admin/users/${userId}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const allowUserResubmission = async (userId) => {
  try {
    await secureFetch(`/api/admin/users/${userId}/allow-resubmission`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return true;
  } catch (error) {
    console.error(error);
    return { ok: false, error: error?.message || 'Failed to allow resubmission' };
  }
};

export const deactivateUser = async (userId) => {
  try {
    await secureFetch(
      `/api/admin/users/${userId}/deactivate`,
      { method: 'PATCH' },
    );
    return true;
  } catch {
    return false;
  }
};

export const reactivateUser = async (userId) => {
  try {
    await secureFetch(
      `/api/admin/users/${userId}/reactivate`,
      { method: 'PATCH' },
    );
    return true;
  } catch {
    return false;
  }
};

export const blacklistClient = async (clientId, reason) => {
  try {
    await secureFetch(`/api/admin/clients/${clientId}/blacklist`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return true;
  } catch (err) {
    return { ok: false, error: err?.message || 'Failed to blacklist client' };
  }
};

export const unblacklistClient = async (clientId) => {
  try {
    await secureFetch(`/api/admin/clients/${clientId}/unblacklist`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return true;
  } catch (err) {
    return { ok: false, error: err?.message || 'Failed to remove blacklist' };
  }
};

export const getOwnerBlacklistedClients = async () => {
  const result = await secureFetch('/api/owner/blacklisted-clients');
  return result?.clients || [];
};

export const getUserKycDocumentUrl = async (userId, docType) => {
  try {
    const result = await secureFetch(`/api/admin/users/${userId}/kyc/${docType}`);
    return result?.url || null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getOwnerBookings = async () => {
  try {
    const result = await secureFetch('/api/owner/bookings');
    return result.items || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getOwnerBookingsPage = async ({ limit = 100, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const result = await secureFetch(`/api/owner/bookings?${params.toString()}`);
    return {
      items: result.items || [],
      hasMore: Boolean(result.hasMore),
      nextOffset: typeof result.nextOffset === 'number' ? result.nextOffset : null,
      error: '',
    };
  } catch (error) {
    console.error(error);
    return { items: [], hasMore: false, nextOffset: null, error: error?.message || 'Failed to fetch owner bookings' };
  }
};

export const approveBooking = async (bookingId) => {
  try {
    await secureFetch(`/api/owner/bookings/${bookingId}/approve`, { method: 'PATCH' });
    return { ok: true };
  } catch (error) {
    console.error(error);
    return { ok: false, message: error?.message || 'Approve failed' };
  }
};

export const completeBooking = async (bookingId) => {
  try {
    const result = await secureFetch(`/api/bookings/${bookingId}/complete`, { method: 'POST' });
    return { ok: true, ...result };
  } catch (error) {
    console.error(error);
    return { ok: false, message: error?.message || 'Complete failed' };
  }
};

export const getOwnerProfile = async () => {
  try {
    const result = await secureFetch('/api/owner/profile');
    return { profile: result.profile || null, error: '' };
  } catch (error) {
    console.error(error);
    return { profile: null, error: error?.message || 'Failed to load profile' };
  }
};

export const updateOwnerProfile = async (payload) => {
  try {
    const result = await secureFetch('/api/owner/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload || {}),
    });
    return { ok: true, profile: result.profile || null, message: result.message || 'Saved' };
  } catch (error) {
    console.error(error);
    return { ok: false, message: error?.message || 'Failed to update profile' };
  }
};

export const getOwnerSettlements = async (since, options = {}) => {
  const { throwOnError = false } = options;
  try {
    const q = since ? `?since=${encodeURIComponent(since)}` : '';
    const result = await secureFetch(`/api/owner/settlements${q}`);
    return result.items || [];
  } catch (error) {
    console.error(error);
    if (throwOnError) throw error;
    return [];
  }
};

export const getOwnerMachineRegistrations = async () => {
  try {
    const result = await secureFetch('/api/owner/machines/registrations');
    return { items: result.items || [], error: '' };
  } catch (error) {
    console.error(error);
    return { items: [], error: error?.message || 'Failed to fetch registrations' };
  }
};

export const updateMachine = async (machineId, updates) => {
  return secureFetch(`/api/owner/machines/${machineId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates || {}),
  });
};

export const getAvailabilityBlocks = async (machineId) => {
  try {
    const result = await secureFetch(`/api/owner/machines/${machineId}/availability-blocks`);
    return { blocks: result.blocks || [], error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { blocks: [], error: error?.message || 'Failed to fetch availability blocks' };
  }
};

export const createAvailabilityBlock = async (machineId, { start_date, end_date, reason } = {}) => {
  try {
    const result = await secureFetch(`/api/owner/machines/${machineId}/availability-blocks`, {
      method: 'POST',
      body: JSON.stringify({
        start_date,
        end_date,
        reason: reason ? String(reason).trim() : null,
      }),
    });
    return { ok: true, block: result.block || null, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { ok: false, block: null, error: error?.message || 'Failed to create availability block' };
  }
};

export const deleteAvailabilityBlock = async (machineId, blockId) => {
  try {
    await secureFetch(`/api/owner/machines/${machineId}/availability-blocks/${blockId}`, {
      method: 'DELETE',
    });
    return { ok: true, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { ok: false, error: error?.message || 'Failed to delete availability block' };
  }
};

export const submitMachineRegistration = async (formData) => {
  let token = localStorage.getItem('developmentexpress_token');
  if (!token) {
    clearAuthStorage();
    throw new Error(AUTH_REQUIRED_ERROR);
  }

  const execute = async (activeToken) => fetch(`${API_BASE_URL}/api/owner/machines/register`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${activeToken}` },
    body: formData,
  });

  let response = await execute(token);
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('developmentexpress_refresh_token');
    if (refreshToken) {
      const refreshResponse = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (refreshResponse.ok) {
        const refreshResult = await refreshResponse.json();
        if (refreshResult?.token) {
          localStorage.setItem('developmentexpress_token', refreshResult.token);
          if (refreshResult.refreshToken) {
            localStorage.setItem('developmentexpress_refresh_token', refreshResult.refreshToken);
          }
          token = refreshResult.token;
          response = await execute(token);
        }
      }
    }
  }

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result?.error || 'Machine registration failed');
  }
  return result;
};

export const getDlqItems = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.queue) params.set('queue', filters.queue);
    if (filters.status) params.set('status', filters.status);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const result = await secureFetch(`/api/admin/dlq${query ? `?${query}` : ''}`);
    return {
      items: result.items || [],
      counters: result.counters || {},
      nextCursor: result.nextCursor || null,
      hasMore: Boolean(result.hasMore),
      error: '',
    };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { items: [], counters: {}, nextCursor: null, hasMore: false, error: error?.message || 'Failed to fetch DLQ items' };
  }
};

export const retryDlqItem = async (id, reason = '') => {
  try {
    await secureFetch(`/api/admin/dlq/${id}/retry`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return true;
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return false;
  }
};

export const getDlqStats = async () => {
  try {
    return await secureFetch('/api/admin/dlq/stats');
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { totalLast24h: 0, failedLast24h: 0, retriedLast24h: 0, queueCounts: {}, hourly: [], error: error?.message || 'Failed to fetch DLQ stats' };
  }
};

export const getRateLimitTelemetry = async () => {
  try {
    return await secureFetch('/api/admin/telemetry/rate-limit');
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { allowed: 0, blocked: 0, byRoute: [], activeBuckets: 0, error: error?.message || 'Failed to fetch telemetry' };
  }
};

export const getApiHealthTelemetry = async () => {
  // Temporarily disabled in production due to endpoint-specific auth mismatch.
  return {
    windowMs: 0,
    totalRequests: 0,
    errors5xx: 0,
    errorRatePct: 0,
    p95Ms: 0,
    p99Ms: 0,
    slo: { p95MsThreshold: 0, errorRatePctThreshold: 0, p95Healthy: true, errorRateHealthy: true },
    byRoute: [],
    error: '',
    unavailableReason: 'disabled',
  };
};

export const acknowledgeSecuritySignal = async (signalId) => {
  try {
    const result = await secureFetch(`/api/admin/security-signals/${signalId}/ack`, { method: 'POST' });
    return {
      ok: true,
      alreadyAcknowledged: Boolean(result?.alreadyAcknowledged),
      acknowledgedBy: result?.acknowledgedBy || null,
      acknowledgedAt: result?.acknowledgedAt || null,
    };
  } catch (error) {
    console.error(error);
    return { ok: false, alreadyAcknowledged: false, acknowledgedBy: null, acknowledgedAt: null };
  }
};

export const getAuditLogs = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.action) params.set('action', filters.action);
    if (filters.actorId) params.set('actorId', filters.actorId);
    if (filters.actorRole) params.set('actorRole', filters.actorRole);
    if (filters.entityType) params.set('entityType', filters.entityType);
    if (filters.metadata) params.set('metadata', filters.metadata);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const result = await secureFetch(`/api/admin/audit-logs${query ? `?${query}` : ''}`);
    return {
      items: result.items || [],
      nextCursor: result.nextCursor || null,
      hasMore: Boolean(result.hasMore),
      error: '',
    };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { items: [], nextCursor: null, hasMore: false, error: error?.message || 'Failed to fetch audit logs' };
  }
};

export const getSurveyResponses = async () => {
  try {
    const result = await secureFetch('/api/admin/survey-responses');
    const items = Array.isArray(result.items) ? result.items : [];
    return { items, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { items: [], error: error?.message || 'Failed to fetch survey responses' };
  }
};

export const approveSurveyResponse = async (id) => {
  try {
    const result = await secureFetch(`/api/admin/survey-responses/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
    });
    return { item: result.item, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { item: null, error: error?.message || 'Approve failed' };
  }
};

export const rejectSurveyResponse = async (id, reason) => {
  try {
    const result = await secureFetch(`/api/admin/survey-responses/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    return { item: result.item, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { item: null, error: error?.message || 'Reject failed' };
  }
};

export const bulkApproveSurveyResponses = async (ids) => {
  try {
    const result = await secureFetch('/api/admin/survey-responses/bulk-approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    return { ...result, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { approved: [], failed: [], error: error?.message || 'Bulk approve failed' };
  }
};

export const bulkRejectSurveyResponses = async (ids, reason) => {
  try {
    const result = await secureFetch('/api/admin/survey-responses/bulk-reject', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, reason }),
    });
    return { ...result, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { rejected: [], failed: [], error: error?.message || 'Bulk reject failed' };
  }
};

export const getSurveySurveyors = async () => {
  try {
    const result = await secureFetch('/api/admin/survey-surveyors');
    const items = Array.isArray(result.items) ? result.items : [];
    return { items, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { items: [], error: error?.message || 'Failed to fetch survey surveyors' };
  }
};

export const saveSurveySurveyor = async (code, name, paymentDetails = {}) => {
  try {
    await secureFetch(`/api/admin/survey-surveyors/${encodeURIComponent(code)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name || '',
        upi_id: paymentDetails.upi_id,
        bank_account: paymentDetails.bank_account,
        bank_ifsc: paymentDetails.bank_ifsc,
      }),
    });
    return { error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { error: error?.message || 'Failed to save surveyor' };
  }
};

export const deleteSurveySurveyor = async (code) => saveSurveySurveyor(code, '');

export const approveSurveySurveyor = async (code) => {
  try {
    const result = await secureFetch(`/api/admin/survey-surveyors/${encodeURIComponent(code)}/approve`, {
      method: 'POST',
    });
    return { item: result.item, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { item: null, error: error?.message || 'Failed to approve surveyor' };
  }
};

export const rejectSurveySurveyor = async (code) => {
  try {
    await secureFetch(`/api/admin/survey-surveyors/${encodeURIComponent(code)}/reject`, {
      method: 'POST',
    });
    return { error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { error: error?.message || 'Failed to reject surveyor' };
  }
};

export const patchSurveySurveyorStatus = async (code, status) => {
  try {
    const result = await secureFetch(`/api/admin/survey-surveyors/${encodeURIComponent(code)}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    return { item: result.item, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { item: null, error: error?.message || 'Failed to update status' };
  }
};

export const submitFreelancerApplication = async ({ name, phone, village }) => {
  try {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    const response = await fetch(`${apiBase}/api/survey/freelancer-apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, phone, village }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { item: null, error: result.error || 'Application failed' };
    }
    return { item: result.item, error: '' };
  } catch (error) {
    console.error(error);
    return { item: null, error: error?.message || 'Application failed' };
  }
};

export const getSurveyPayoutRate = async () => {
  try {
    const result = await secureFetch('/api/admin/survey-settings/rate');
    return { ratePerForm: Number(result.ratePerForm) || 30, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { ratePerForm: 30, error: error?.message || 'Failed to fetch rate' };
  }
};

export const saveSurveyPayoutRate = async (value) => {
  try {
    const result = await secureFetch('/api/admin/survey-settings/rate', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    return { ratePerForm: Number(result.ratePerForm), error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { ratePerForm: null, error: error?.message || 'Failed to save rate' };
  }
};

export const getSurveyPayoutSummary = async (from, to) => {
  try {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    const result = await secureFetch(`/api/admin/survey-payouts${qs ? `?${qs}` : ''}`);
    return { summary: result, error: '' };
  } catch (error) {
    if (!isAuthRequiredError(error)) console.error(error);
    return { summary: null, error: error?.message || 'Failed to load payout summary' };
  }
};

export const exportSurveyPayoutCsv = async (from, to) => {
  try {
    const token = localStorage.getItem('developmentexpress_token');
    if (!token) return { error: 'auth_required' };
    const response = await fetch(`${API_BASE_URL}/api/admin/survey-payouts/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ from, to }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        error: payload.error || 'Export failed',
        code: payload.code,
        missingPaymentDetails: payload.missingPaymentDetails,
      };
    }
    return { ...payload, error: '' };
  } catch (error) {
    console.error(error);
    return { error: error?.message || 'Export failed' };
  }
};

